import { GQL_ID, Product } from '@@types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'

import { useCartActions, useCartStore } from '@entities/Cart'
import {
  getAmountStep,
  getCountPostfix,
  getMaxAmount,
  getMinAmount
} from '@entities/Product/helpers'

import { useAuth } from '@features/auth'
import useCartItemTotal from '@features/cart/hooks/useCartItemTotal'
import { useErrorInfoModal } from '@features/modal'

import { useAnalytics } from '@shared/lib/analytics'
import { cn } from '@shared/lib/cn'
import { formatPrice } from '@shared/uttils'
import { convertToCartQuant, convertToUIQuant, normalizeAmountWithUIQuant } from '@shared/uttils/uiQuantHelpers'


import styles from './CartQuantityControl.module.scss'

interface CartQuantityControlProps {
  shopId: GQL_ID;
  className?: string;
  size?: 'small';
  product: Product;
  invertInfo?: boolean;
}

export default function CartQuantityControl (props: CartQuantityControlProps) {
  const { t: tc } = useTranslation('common')
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()
  const { className, size, product, invertInfo } = props
  const { getProduct } = useCartStore()
  const cartItem = getProduct(product.id)
  const { isAuthorized } = useAuth()
  const { updateProductAmount } = useCartActions()
  const { addToCart: trackAddToCart, removeFromCart: trackRemoveFromCart } = useAnalytics()

  const amount = useMemo(() => cartItem?.amount || 0, [ cartItem?.amount ])
  const uiQuant = product.priceOffer?.uiQuant || 'XPC'
  const uiAmount = useMemo(() => convertToUIQuant(amount, uiQuant), [ amount, uiQuant ])
  const step = getAmountStep(product)
  const { maxAmount } = getMaxAmount(product)
  const minAmount = getMinAmount(product)
  const uiMinAmount = convertToUIQuant(minAmount, uiQuant)
  const uiMaxAmount = convertToUIQuant(maxAmount, uiQuant)
  const cartItemTotalPrice = useCartItemTotal(product, amount)
  
  // Состояние для inline редактирования
  const [ isEditing, setIsEditing ] = useState(false)
  const [ inputValue, setInputValue ] = useState('')
  
  // Определяем, что следующее уменьшение удалит товар
  const willRemoveOnDecrement = useMemo(() => {
    const newUIAmount = uiAmount - step
    return newUIAmount < uiMinAmount
  }, [ uiAmount, step, uiMinAmount ])

  // Обработчик клика на центральную часть
  const handleCenterClick = () => {
    setInputValue(uiAmount.toString())
    setIsEditing(true)
  }

  // Обработчик изменения input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value
    // Заменяем запятую на точку
    value = value.replace(',', '.')
    // Разрешаем только цифры и одну точку
    if (/^\d*\.?\d*$/.test(value)) {
      setInputValue(value)
    }
  }

  // Обработчик подтверждения
  const handleConfirmInput = async () => {
    const numValue = parseFloat(inputValue) || 0
    const normalizedServerAmount = normalizeAmountWithUIQuant(numValue, step, uiQuant)
    
    if (normalizedServerAmount >= minAmount && normalizedServerAmount <= maxAmount) {
      // Track analytics based on quantity change
      const quantityDiff = normalizedServerAmount - amount
      if (quantityDiff > 0) {
        trackAddToCart(product, quantityDiff)
      }
      else if (quantityDiff < 0) {
        trackRemoveFromCart(product, Math.abs(quantityDiff))
      }
      
      try {
        await updateProductAmount(product.id, normalizedServerAmount, isAuthorized)
      }
      catch (error) {
        console.error('Error updating cart:', error)
        openErrorInfoModal({
          description: te('update_cart_item'),
        })
      }
    }
    
    setIsEditing(false)
  }

  // Обработчик отмены
  const handleCancelInput = () => {
    setIsEditing(false)
    setInputValue('')
  }

  const handleIncrement = async () => {
    const newUIAmount = uiAmount + step
    const newServerAmount = convertToCartQuant(newUIAmount, uiQuant)

    if (newServerAmount <= maxAmount) {
      // Track analytics: adding more items
      trackAddToCart(product, step)
      
      try {
        await updateProductAmount(product.id, newServerAmount, isAuthorized)
      }
      catch (error) {
        console.error('Error updating cart:', error)
        openErrorInfoModal({
          description: te('update_cart_item'),
        })
      }
    }
  }

  const handleDecrement = async () => {
    const newUIAmount = uiAmount - step
    const newServerAmount = convertToCartQuant(newUIAmount, uiQuant)

    // Если новое значение меньше минимального - удаляем товар (amount = 0)
    const finalAmount = newServerAmount >= minAmount ? newServerAmount : 0

    // Track analytics: removing items
    const quantityRemoved = finalAmount === 0 ? amount : step
    trackRemoveFromCart(product, quantityRemoved)

    try {
      await updateProductAmount(product.id, finalAmount, isAuthorized)
    }
    catch (error) {
      console.error('Error updating cart:', error)
      openErrorInfoModal({
        description: te(finalAmount === 0 ? 'drop_cart_item' : 'update_cart_item'),
      })
    }
  }

  const unit = getCountPostfix(product, tc)
  const value = uiAmount

  return (
    <div
      className={cn(
        styles.root,
        className,
        size ? styles[size] : null,
        invertInfo ? 'product_counter_tabbar' : null,
        { [styles.editing]: isEditing },
      )}
    >
      {!isEditing && (
        <button
          className={cn(
            styles['root__button'],
            'reset-button',
            styles.root__dec,
            {
              [styles.disabled]: uiAmount <= 0,
              [styles.delete]: willRemoveOnDecrement,
            },
          )}
          type="button"
          onClick={handleDecrement}
          disabled={uiAmount <= 0}
        >
          {willRemoveOnDecrement && (
            <ReactSVG
              src="/icons/trash.svg"
              wrapper="span"
              className={styles.root__deleteIcon}
            />
          )}
        </button>
      )}
      
      <div className={styles.root__info} onClick={!isEditing ? handleCenterClick : undefined}>
        {isEditing ? (
          <div className={styles.root__inputWrapper}>
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              className={styles.root__input}
              autoFocus
              onBlur={() => {
                // Небольшая задержка, чтобы кнопка подтверждения успела отработать
                setTimeout(() => {
                  if (isEditing) {
                    handleCancelInput()
                  }
                }, 150)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleConfirmInput()
                }
                if (e.key === 'Escape') {
                  e.preventDefault()
                  handleCancelInput()
                }
              }}
            />
            <span className={styles.root__inputUnit}>{unit}</span>
          </div>
        ) : (
          <>
            {invertInfo ? (
              <>
                <b>
                  {value} {unit}
                </b>
                {!!cartItemTotalPrice && (
                  <span>{formatPrice(cartItemTotalPrice)}</span>
                )}
              </>
            ) : (
              <>
                {!!cartItemTotalPrice && <b>{formatPrice(cartItemTotalPrice)}</b>}
                <span>
                  {value} {unit}
                </span>
              </>
            )}
          </>
        )}
      </div>
      
      {isEditing ? (
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault() // Предотвращаем потерю фокуса с input
            handleConfirmInput()
          }}
          className={cn(
            styles['root__button'],
            'reset-button',
            styles.root__confirmButton
          )}
        >
          ✓
        </button>
      ) : (
        <button
          className={cn(
            styles['root__button'],
            'reset-button',
            styles.root__inc,
            {
              [styles.disabled]: uiAmount >= uiMaxAmount,
            },
          )}
          type="button"
          onClick={handleIncrement}
          disabled={uiAmount >= uiMaxAmount}
        ></button>
      )}
    </div>
  )
}